import React, { useState, useEffect, useRef } from 'react';
import { FaBars, FaBell, FaEnvelope, FaUser } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import './InternalHeader.css';

const InternalHeader = ({ moduleName, toggleSidebar }) => {
    const [showEmailDropdown, setShowEmailDropdown] = useState(false);
    const [showNotificationDropdown, setShowNotificationDropdown] = useState(false);
    const [showProfileDropdown, setShowProfileDropdown] = useState(false);
    const [showLogoutModal, setShowLogoutModal] = useState(false);
    const [notifications, setNotifications] = useState({
        messages: [],
        appointments: []
    });

    const navigate = useNavigate();
    const emailDropdownRef = useRef(null);
    const notificationDropdownRef = useRef(null);
    const profileDropdownRef = useRef(null);
    const headerRef = useRef(null);
    const [currentUserId, setCurrentUserId] = useState(null);

    useEffect(() => {
        const fetchCurrentUser = async () => {
            try {
                const response = await fetch('https://api.hgcdental.clinic/api/profile', {
                    credentials: 'include',
                });
                if (!response.ok) throw new Error("Failed to fetch current user");

                const data = await response.json();
                setCurrentUserId(data.id);
            } catch (error) {
                console.error("Error fetching profile:", error);
            }
        };

        fetchCurrentUser();
    }, []);

    useEffect(() => {
        const fetchNotifications = async () => {
            try {
                if (!currentUserId) return;
                const response = await fetch("https://api.hgcdental.clinic/api/notifications");
                const all = await response.json();
                const userNotifications = all.filter(
                    n => n.receiver?.toString() === currentUserId?.toString()
                );

                const messages = userNotifications
                    .filter(n => n.type === "message")
                    .sort((a, b) => new Date(b.date) - new Date(a.date));

                const appointments = userNotifications
                    .filter(n => n.type === "low_stock")
                    .sort((a, b) => new Date(b.date) - new Date(a.date));

                setNotifications({ messages, appointments });
            } catch (error) {
                console.error("Notification fetch error:", error);
            }
        };

        const interval = setInterval(fetchNotifications, 30000);
        fetchNotifications();
        return () => clearInterval(interval);
    }, [currentUserId]);

    const handleLogout = () => {
        window.location.href = '/';
    };

    const toggleEmailDropdown = () => {
        setShowEmailDropdown(!showEmailDropdown);
        setShowNotificationDropdown(false);
        setShowProfileDropdown(false);
    };

    const toggleNotificationDropdown = () => {
        setShowNotificationDropdown(!showNotificationDropdown);
        setShowEmailDropdown(false);
        setShowProfileDropdown(false);
    };

    const toggleProfileDropdown = () => {
        setShowProfileDropdown(!showProfileDropdown);
        setShowEmailDropdown(false);
        setShowNotificationDropdown(false);
    };

    const handleProfileClick = () => {
        navigate('/my-profile');
    };

    const formatDate = (date) => {
        const options = { weekday: 'short', month: 'short', day: 'numeric' };
        return new Date(date).toLocaleDateString('en-US', options);
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                headerRef.current && !headerRef.current.contains(event.target)
            ) {
                setShowEmailDropdown(false);
                setShowNotificationDropdown(false);
                setShowProfileDropdown(false);
            }
        };

        document.addEventListener('click', handleClickOutside);
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, []);

    return (
        <header className="internal-header" ref={headerRef}>
            <div className="menu-icon" onClick={toggleSidebar}>
                <FaBars size={25} className="bars-icon" />
            </div>
            <div className="module-name-logo">
                <h1 className="module-name">{moduleName}</h1>
            </div>
            <div className="auth">
                <div className="icon-wrapper">
                    <FaEnvelope size={25} className="email-icon" onClick={toggleEmailDropdown} />
                    <FaBell size={25} className="notification-icon" onClick={toggleNotificationDropdown} />
                    <FaUser size={24} className="profile-icon" onClick={toggleProfileDropdown} />
                </div>
            </div>
            {showEmailDropdown && (
                <div className="dropdown email-dropdown" ref={emailDropdownRef}>
                    <h3 className="email-dropdown-title">Emails</h3>
                    {notifications.messages.map((msg) => (
                        <div key={msg.id} className="notification-item">
                            <div className="header-profile-image"></div>
                            <div className="message-content">
                                <span className="message-sender">{msg.name}</span>
                                <span className="message-text">{msg.message}</span>
                            </div>
                            <span className="message-time">{msg.time}</span>
                        </div>
                    ))}
                    <a href='/chat'><div className="view-all">See all messages</div></a>
                </div>
            )}
            {showNotificationDropdown && (
                <div className="dropdown notification-dropdown" ref={notificationDropdownRef}>
                    <h3 className="notification-dropdown-title">Notifications</h3>
                    {notifications.appointments.map((notification) => (
                        <div key={notification._id || notification.id} className="notification-item">
                            <div className="message-content">
                                <span className="appointment-date">
                                    {formatDate(notification.date)}
                                </span>
                                <span className="appointment-name">{notification.message}</span>
                            </div>
                            {/* Optional: display type or more metadata */}
                        </div>
                    ))}
                </div>
            )}
            {showProfileDropdown && (
                <div className="dropdown profile-dropdown" ref={profileDropdownRef}>
                    <div onClick={handleProfileClick} className="dropdown-item">My Profile</div>
                    <div onClick={() => setShowLogoutModal(true)} className="dropdown-item">Log Out</div>
                </div>
            )}
            {showLogoutModal && (
                <div className="logout-modal">
                    <div className="logout-modal-content">
                        <div className="minus-margin" />
                        <h2>Confirm Logout</h2>
                        <p>Are you sure you want to log out?</p>
                        <div className="logout-buttons">
                            <button className="logout-cancel " onClick={() => setShowLogoutModal(false)}>Cancel</button>
                            <button className="logout-confirm " onClick={handleLogout}>Log Out</button>
                        </div>
                    </div>
                </div>
            )}
        </header>
    );
};

export default InternalHeader;
